import { compressToUTF16, decompressFromUTF16 } from 'lz-string'
import { getFile } from '../api'
import {
  CompressedFile,
  Cut,
  CutTag,
  DateFormat,
  DynamoFile,
  FileRecord,
  FileStatus,
  NextCell,
  Rectangle,
  TemplateDrawing,
  TestDataOcr,
  TextcutRectangle,
  UndoEvent,
  UserGeneratedLine,
  UserSettings,
} from '../types'
import {
  DISABLED_RIBBON_BUTTON,
  ENABLED_RIBBON_BUTTON_AFTER_SEARCH_ALL,
  NO_OUTPUT,
  USER_SETTINGS,
  MAX_DIGITS_COUNT,
} from '../constant'
import {
  createTextcutRectangle,
  fileSizeLessThan5MB,
  getCutTypeStyle,
  publishBindingOnDataChangeEvent,
  publishOnReferenceSelectedEvent,
} from '../utils/common'
import dayjs from 'dayjs'
import { breakDownOcr } from '../utils/ocr'
import { getDateBlocks } from '../utils/fuzzy'
import { addFileToDB, removeFileFromDB } from '../utils/db'
import { nanoid } from 'nanoid'
import {
  extractAndParseDate,
  isDateOnly,
  isValidDate,
} from '../utils/date-util'
import TextcutRectangleBuilder from '../utils/TextcutRectangleBuilder'
import {
  calculateHeightandWidth,
  findTOpLeftPointFromVertices,
} from '../utils/spatial'
import {
  extractNumericValueFromAmount,
  isCurrencyOrNumericOnly,
  isLargeNumber
} from '../utils/currency'
import { fromPromise, ResultAsync } from 'neverthrow'
import { errorFormatter, errorStringify } from '../utils/error'

/**
 * Convert a string to a numeric value if it does not exceed 9 digits in the integer part
 */
export const getNumericValue = (value: any): string | number => {
  const parsedValue = extractNumericValueFromAmount(value).toString()
  const [integerPart] = parsedValue.split('.')
  if (integerPart && integerPart.length > MAX_DIGITS_COUNT) {
    return parsedValue
  }
  const num = Number(parsedValue)
  if (!isNaN(num)) {
    return num
  }
  return value
}

/**
 * Test function, set values a range of cells
 */
export const setValuesForRangeDemo = async () => {
  await Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet()
    const data = [['foo', 'bar', 'qux']]
    const range = sheet.getRange('B5:D5')
    range.values = data
    await ctx.sync()
  })
}

export const setValueToSelectedRange = async (val: string) => {
  return await Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet()
    sheet.load('id')
    const range = ctx.workbook.getSelectedRange()
    range.load('values')
    range.load('rowIndex')
    range.load('columnIndex')
    await ctx.sync()
    const values = range.values
    values[0][0] = val
    range.values = values
    range.format.autofitColumns()
    const row = range.rowIndex
    const col = range.columnIndex
    await ctx.sync()
    return `${sheet.id}/${numberToLetters(col)}${row + 1}`
  })
}

export const generateExcelColumns = (): string[] => {
  const arr: string[] = [NO_OUTPUT]
  for (let i = 0; i < 50; i++) arr.push(numberToLetters(i))

  return arr
}

export const numberToLetters = (num: number) => {
  let letters = ''
  while (num >= 0) {
    letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[num % 26] + letters
    num = Math.floor(num / 26) - 1
  }
  return letters
}

export const setMatrixToSelectedRange = async (
  matrix: string[][],
  row: number,
  col: number
) => {
  return await Excel.run(async (ctx) => {
    const range = ctx.workbook.getSelectedRange()
    const sheet = ctx.workbook.worksheets.getActiveWorksheet()
    range.load('columnIndex')
    range.load('rowIndex')
    sheet.load('id')
    await ctx.sync()
    const [startRow, startCol] = [range.rowIndex, range.columnIndex]
    const [endRow, endCol] = [startRow + row - 1, startCol + col - 1]

    const matrixRange = `${numberToLetters(startCol)}${
      startRow + 1
    }:${numberToLetters(endCol)}${endRow + 1}`

    const outputRange = ctx.workbook.worksheets
      .getActiveWorksheet()
      .getRange(matrixRange)
    outputRange.values = matrix
    outputRange.format.autofitColumns()
    outputRange.format.autofitRows()
    await ctx.sync()
    return `${sheet.id}/${matrixRange}`
  })
}

export const generateReferenceId = (sheetId: string, cellAddr: string) =>
  `REFERENCE>>${sheetId}>>${cellAddr}`

export const lettersToNumber = (letters: string) => {
  let n = 0
  for (let p = 0; p < letters.length; p++) {
    n = letters[p].charCodeAt(0) - 64 + n * 26
  }
  return n
}

export const cellInRange = (cellAddr: string, rangeAddr: string) => {
  try {
    const cellCol = lettersToNumber(cellAddr[0])
    const cellRow = Number(cellAddr.substring(1, cellAddr.length))
    const [head, tail] = rangeAddr.split(':')
    const headCol = lettersToNumber(head[0])
    const headRow = Number(head.substring(1, head.length))
    const tailCol = lettersToNumber(tail[0])
    const tailRow = Number(tail.substring(1, tail.length))
    return (
      cellCol >= headCol &&
      cellCol <= tailCol &&
      cellRow >= headRow &&
      cellRow <= tailRow
    )
  } catch (err) {
    console.log('err:', err)
    return false
  }
}

/**
 * Loads values from a specified range in the workbook.
 * @param range - The range to load values from.
 *
 * @returns A promise that resolves to the loaded values from the range.
 */
export const loadValuesFromARange = (range: string) => {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet()
    const r = sheet.getRange(range)
    // r.load('values')
    r.load('text') // using text instead of values due to formatting issues
    await ctx.sync()
    // return r.values
    return r.text.map((row) => row.map((text) => text.trim()))
  })
}

export const loadTextFromRange = (range: string, outputRange: string[]) =>
  Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet()
    const r = sheet.getRange(range)
    r.load(['text', 'values', 'numberFormat'])
    await ctx.sync()
    const noOuputIndices: number[] = []
    outputRange.forEach((r, i) => {
      if (r === NO_OUTPUT) noOuputIndices.push(i)
    })
    const arr = r.text.map((row) => row.map((text) => text.trim()))
    const valueArr = r.values.map((row) => row.map((val) => val))
    const numberFormatArr = r.numberFormat.map((row) => row.map((val) => val))
    const result: string[][] = new Array(arr.length).fill(null).map((_) => [])
    const valueResult: any[][] = new Array(arr.length).fill(null).map((_) => [])
    const numberFormat: string[][] = new Array(arr.length)
      .fill(null)
      .map((_) => [])
    for (let i = 0; i < arr.length; i++) {
      for (let j = 0; j < arr[i].length; j++) {
        if (noOuputIndices.includes(j)) continue
        result[i].push(arr[i][j])
        valueResult[i].push(valueArr[i][j])
        numberFormat[i].push(numberFormatArr[i][j])
      }
    }
    return {
      textResult: result,
      valueResult: valueResult,
      numberFormat,
    }
  })

// const decode = (buffer: ArrayBuffer) => {
//   const decoder = new TextDecoder()
//   return decoder.decode(buffer)
// }

// const encode = (str: string) => {
//   const encoder = new TextEncoder()
//   return encoder.encode(str).buffer
// }

const attachPDFsToWorkbook = (files: CompressedFile[]) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    files.forEach((file) => {
      const k = `FILE_${file.fileId}`
      settings.add(k, JSON.stringify(file))
    })
    await ctx.sync()
  })

export const pdfToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

export const base64ToBlob = (base64: string, type: string) => {
  const chars = atob(base64.split(',')[1])
  const numbers = new Array(chars.length)
  for (let i = 0; i < chars.length; i++) {
    numbers[i] = chars.charCodeAt(i)
  }
  const byteArray = new Uint8Array(numbers)
  return new Blob([byteArray], { type })
}

export const savePdfsToWorkbook = async (files: DynamoFile[]) => {
  const [cloudFileRecords] = await getLocalFileRecords()
  const filteredFiles = files.filter(
    (file) =>
      cloudFileRecords.some((re) => re.fileId === file.fileId) &&
      fileSizeLessThan5MB(file.originalFileSize)
  )
  const filteredFileIdSet = new Set(filteredFiles.map((file) => file.fileId))
  const promises = filteredFiles.map(async (file) => {
    const [pdfBlob, ocrJSON] = await getFile(file.fileId)
    const compressedOCR = compressToUTF16(JSON.stringify(ocrJSON))
    const pdfBase64 = await pdfToBase64(pdfBlob)
    const compressedPDF = compressToUTF16(pdfBase64)
    const ocrSize = new Blob([compressedOCR]).size

    return {
      ...file,
      compressedPDF,
      compressedOCR,
      originalFileSize: pdfBlob.size + ocrSize,
    }
  })
  const newFileRecords = cloudFileRecords.map((record) => {
    if (filteredFileIdSet.has(record.fileId)) {
      return { ...record, isLocal: true }
    }
    return record
  })
  // console.log('newFileRecords:', newFileRecords)
  await updateLocalFileRecords(newFileRecords)
  const arr = await Promise.all(promises)
  await attachPDFsToWorkbook(arr)
}

export const fetchPDFsFromWorkbook = () =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    settings.load('items')
    await ctx.sync()
    const items = settings.items
    const settingArray: Excel.Setting[] = []
    for (const setting of items) {
      settingArray.push(setting)
      setting.load(['value', 'key', 'isNullObject'])
    }
    await ctx.sync()
    let arr: DynamoFile[] = []
    settingArray
      .filter((item) => !item.isNullObject && item.key.startsWith('FILE_'))
      .forEach((item) => {
        const parsedValue: CompressedFile = JSON.parse(item.value)
        arr.push({
          completedAt: parsedValue.completedAt,
          createdAt: parsedValue.createdAt,
          fileId: parsedValue.fileId,
          fileType: parsedValue.fileType,
          type: parsedValue.type,
          folderName: parsedValue.folderName,
          parentFolderId: parsedValue.parentFolderId,
          ocrFileKey: parsedValue.ocrFileKey,
          originalFileKey: parsedValue.originalFileKey,
          status: parsedValue.status,
          userId: parsedValue.userId,
          fileName: parsedValue.fileName,
          originalFileSize: parsedValue.originalFileSize,
          dir: parsedValue.dir,
          extractionEngine: parsedValue.extractionEngine,
          extractionMethod: parsedValue.extractionMethod,
        })
      })
    // if (!item.isNullObject) {
    //   const parsedValue: CompressedFile[] = JSON.parse(item.value)
    //   for (const file of parsedValue) {
    //     arr.push({
    //       completedAt: file.completedAt,
    //       createdAt: file.createdAt,
    //       fileId: file.fileId,
    //       fileType: file.fileType,
    //       ocrFileKey: file.ocrFileKey,
    //       originalFileKey: file.originalFileKey,
    //       status: file.status,
    //       userId: file.userId,
    //       fileName: file.fileName,
    //     })
    //   }
    // }
    return arr.sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)))
  })

export const fetchPDFFromLocal = (id: string) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    settings.load('items')
    await ctx.sync()
    const items = settings.items
    const settingArray: Excel.Setting[] = []
    for (const setting of items) {
      settingArray.push(setting)
      setting.load(['value', 'key', 'isNullObject'])
    }
    await ctx.sync()
    const filteredSettingArray = settingArray.filter(
      (setting) => !setting.isNullObject && setting.key.startsWith('FILE_')
    )
    for (const item of filteredSettingArray) {
      const key = item.key
      const value = item.value
      const [, fileId] = key.split('_')

      if (fileId !== id) continue
      const parsedValue: CompressedFile = JSON.parse(value)
      const decompressedOCR = decompressFromUTF16(parsedValue.compressedOCR)
      const ocr: TestDataOcr = JSON.parse(decompressedOCR)
      const decompressedPDF = decompressFromUTF16(parsedValue.compressedPDF)
      const pdfBlob = base64ToBlob(decompressedPDF, 'application/pdf')
      return { ...parsedValue, ocr, pdfBlob }
    }
    return null
  })

export const fetchPDFBatchFromLocal = (ids: string[]) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    settings.load('items')
    await ctx.sync()
    const items = settings.items
    const settingArray: Excel.Setting[] = []
    for (const setting of items) {
      settingArray.push(setting)
      setting.load(['value', 'key', 'isNullObject'])
    }
    await ctx.sync()
    const filteredSettingArray = settingArray.filter(
      (setting) => !setting.isNullObject && setting.key.startsWith('FILE_')
    )
    const arr: (CompressedFile & { ocr: TestDataOcr; pdfBlob: Blob })[] = []
    for (const item of filteredSettingArray) {
      const key = item.key
      const value = item.value
      const [, fileId] = key.split('_')

      if (ids.includes(fileId)) {
        const parsedValue: CompressedFile = JSON.parse(value)
        const decompressedOCR = decompressFromUTF16(parsedValue.compressedOCR)
        const ocr: TestDataOcr = JSON.parse(decompressedOCR)
        const decompressedPDF = decompressFromUTF16(parsedValue.compressedPDF)
        const pdfBlob = base64ToBlob(decompressedPDF, 'application/pdf')
        arr.push({ ...parsedValue, ocr, pdfBlob })
      }
    }
    return arr
  })

export const removeAttachedFile = (id: string) =>
  Excel.run(async (ctx) => {
    await removeFileFromDB(id)
    const settingId = `FILE_${id}`
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject(settingId)
    item.load('isNullObject')
    await ctx.sync()
    if (!item.isNullObject) {
      item.delete()
      await ctx.sync()
    }
  })

export const autoSave = () =>
  Excel.run(async (ctx) => {
    await ctx.sync()
    ctx.workbook.save()
  })

export const replaceLocalPDF = (
  file: CompressedFile & { ocr: TestDataOcr; pdfBlob: Blob }
) =>
  Excel.run(async (ctx) => {
    const compressedOCR = compressToUTF16(JSON.stringify(file.ocr))
    const ocrSize = new Blob([compressedOCR]).size
    const pdfBase64 = await pdfToBase64(file.pdfBlob)
    const compressedPDF = compressToUTF16(pdfBase64)
    const settings = ctx.workbook.settings
    const k = `FILE_${file.fileId}`
    const item = settings.getItemOrNullObject(k)
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) return
    const value: CompressedFile = JSON.parse(item.value)
    const newValue = {
      ...value,
      compressedOCR,
      compressedPDF,
      originalFileSize: file.pdfBlob.size + ocrSize,
    }
    settings.add(k, JSON.stringify(newValue))
    await ctx.sync()
    ctx.workbook.save()
  })

export const saveFileRecordsToLocal = (fileRecords: FileRecord[]) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('FileRecords')
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) {
      settings.add('FileRecords', JSON.stringify(fileRecords))
    } else {
      const elems: FileRecord[] = JSON.parse(item.value)
      const arr = [...elems, ...fileRecords]
      settings.add('FileRecords', JSON.stringify(arr))
    }
    await ctx.sync()
    ctx.workbook.save(Excel.SaveBehavior.prompt)
  })

export const updateLocalFileRecords = (fileRecords: FileRecord[]) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('FileRecords')
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) {
      settings.add('FileRecords', JSON.stringify(fileRecords))
    } else {
      const elems: FileRecord[] = JSON.parse(item.value)
      const arr: [string, FileRecord][] = fileRecords.map((record) => [
        record.fileId,
        record,
      ])
      const map = new Map(arr)
      const mapped: FileRecord[] = elems.map((elem) => {
        if (map.has(elem.fileId)) {
          const item = map.get(elem.fileId)!
          const newElem: FileRecord = {
            ...elem,
            status: item.status,
            isLocal: item.isLocal,
          }
          return newElem
        }
        return elem
      })
      // const filtered = mapped.filter(
      //   (elem) => elem.status !== FileStatus.FAILED
      // )
      // console.log('mapped:', mapped)
      settings.add('FileRecords', JSON.stringify(mapped))
    }
    await ctx.sync()
    ctx.workbook.save()
  })

export const containProcessingRecord = (): Promise<[boolean, FileRecord[]]> =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('FileRecords')
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) return [false, []]
    const elems: FileRecord[] = JSON.parse(item.value)
    const arr = elems.filter((elem) => elem.status === FileStatus.PROCESSING)
    return [arr.length > 0 ? true : false, arr]
  })

export const getLocalFileRecords = (): Promise<[FileRecord[], FileRecord[]]> =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('FileRecords')
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) return [[], []]
    else {
      const elems: FileRecord[] = JSON.parse(item.value)
      const localRFecords = elems.filter((elem) => elem.isLocal)
      const cloudRecords = elems.filter((elem) => !elem.isLocal)
      return [cloudRecords, localRFecords]
    }
  })

export const findLocalFileRecordWithFileId = (fileId: string) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('FileRecords')
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) return undefined
    else {
      const elems: FileRecord[] = JSON.parse(item.value)
      return elems.find((elem) => elem.fileId === fileId)
    }
  })

export const findLocalFileRecordWithFileIds = (fileIds: string[]) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('FileRecords')
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) return []
    const elems: FileRecord[] = JSON.parse(item.value)
    return elems.filter((elem) => fileIds.includes(elem.fileId))
  })

export const removeLocalFileRecordWithIds = (fileIds: string[]) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('FileRecords')
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) return
    const elems: FileRecord[] = JSON.parse(item.value)
    const filtered = elems.filter((elem) => !fileIds.includes(elem.fileId))
    settings.add('FileRecords', JSON.stringify(filtered))
    await ctx.sync()
    ctx.workbook.save()
  })

export const resetLocalFileRecord = () =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('FileRecords')
    item.load(['value', 'isNullObject'])
    await ctx.sync()
    if (item.isNullObject) return
    const arr: FileRecord[] = []
    settings.add('FileRecords', JSON.stringify(arr))
    await ctx.sync()
    ctx.workbook.save()
  })

export const undoRange = (event: UndoEvent) =>
  Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(
      event.reference.sheetId
    )
    sheet.load('isNullObject')
    await ctx.sync()
    if (sheet.isNullObject) return
    sheet.activate()
    const range = sheet.getRange(event.reference.rangeAddr)
    range.values = event.preValues
    range.format.fill.clear()
    if (event.reference.tag === CutTag.TABLE) {
      range.format.borders.getItem('EdgeBottom').style = 'None'
      range.format.borders.getItem('EdgeLeft').style = 'None'
      range.format.borders.getItem('EdgeRight').style = 'None'
      range.format.borders.getItem('EdgeTop').style = 'None'
    }
    range.select()
    await ctx.sync()
  })

export const getRangeValues = (sheetId: string, rangeAddress: string) =>
  Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(sheetId)
    sheet.load('isNullObject')
    await ctx.sync()
    if (sheet.isNullObject) throw new Error('Invalid range')
    const range = sheet.getRange(rangeAddress)
    range.load('values')
    await ctx.sync()
    return range.values
  })

export const getRangeText = (sheetId: string, rangeAddress: string) =>
  Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(sheetId)
    sheet.load('isNullObject')
    await ctx.sync()
    if (sheet.isNullObject) throw new Error('Invalid range')
    const range = sheet.getRange(rangeAddress)
    range.load('text')
    await ctx.sync()
    return range.text
  })

export const setRangeValues = (
  sheetId: string,
  rangeAddress: string,
  values: any[][]
) =>
  Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(sheetId)
    sheet.load('isNullObject')
    await ctx.sync()
    if (sheet.isNullObject) throw new Error('Invalid range')
    const range = sheet.getRange(rangeAddress)
    range.values = values
    sheet.activate()
    range.select()
    await ctx.sync()
  })

export const disableRibbon = () =>
  Office.ribbon.requestUpdate(DISABLED_RIBBON_BUTTON)

export const enableRibbonAfterSearchAll = () =>
  Office.ribbon.requestUpdate(ENABLED_RIBBON_BUTTON_AFTER_SEARCH_ALL)

export const removeInvalidBindings = () =>
  Excel.run(async (ctx) => {
    const ids: string[] = []
    const bindings = ctx.workbook.bindings
    const settings = ctx.workbook.settings
    const item = settings.getItemOrNullObject('REFERENCES')
    bindings.load('items')
    item.load(['isNullObject', 'value'])
    await ctx.sync()
    if (item.isNullObject) {
      return []
    }
    const elems: TextcutRectangle[] = JSON.parse(item.value)
    // remove invalid bindings
    for (const item of bindings.items) {
      item.load('id')
      await ctx.sync().catch(() => ids.push(item.id))
    }
    // remove bindings which associated references are empty
    const ranges = elems.map((elem) => {
      const sheet = ctx.workbook.worksheets.getItemOrNullObject(elem.sheetId)
      const range = sheet.getRange(elem.rangeAddr)
      range.load(['values', 'isNullObject'])
      return range
    })
    await ctx.sync()
    for (let i = 0; i < ranges.length; i++) {
      if (ranges[i].isNullObject) {
        ids.push(elems[i].bindingId)
      } else if (
        ranges[i].values.every((row) => row.every((value) => value === ''))
      ) {
        ids.push(elems[i].bindingId)
      }
    }
    const filtered = elems.filter((elem) => {
      if (elem.bindingGroupId) {
        const inIds = ids.includes(elem.bindingGroupId)
        if (inIds) {
          return false
        }
      }

      return !ids.includes(elem.bindingId)
    })
    settings.add('REFERENCES', JSON.stringify(filtered))
    console.log('ids:', ids)
    for (const id of ids) Office.context.document.bindings.releaseByIdAsync(id)
    await ctx.sync().catch((err) => {
      console.error('removeInvalidBindings error:', err)
    })
    return ids
  })

export const releaseBindingIdAsync = (id: string) =>
  new Promise((resolve, reject) =>
    Office.context.document.bindings.releaseByIdAsync(id, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result)
      } else {
        reject(result)
      }
    })
  )

export const createBindingAndAddOnSelectionHandler = (
  ctx: Excel.RequestContext,
  range: Excel.Range,
  bindingType: 'Range' | 'Table' | 'Text',
  bindingId: string
) => {
  const binding = ctx.workbook.bindings.add(range, bindingType, bindingId)
  binding.onSelectionChanged.add(async (args) => {
    args.binding.load('id')
    await args.binding.context.sync()
    publishOnReferenceSelectedEvent(args.binding.id)
  })

  // binding.onDataChanged.add(async (args) => {
  //   args.binding.load('id')
  //   await args.binding.context.sync()
  //   console.log('from onDataChanged:', args)
  //   publishBindingOnDataChangeEvent(args.binding.id)
  // })
  return binding
}

export const deleteBinding = (bindingId: string) =>
  Excel.run(async (ctx) => {
    const binding = ctx.workbook.bindings.getItemOrNullObject(bindingId)
    binding.load(['id', 'isNullObject'])
    await ctx
      .sync()
      .catch(() =>
        Office.context.document.bindings.releaseByIdAsync(binding.id)
      )
    if (binding.isNullObject) return
    binding.delete()
    await ctx.sync()
  })

export const recreateBinding = (
  bindingId: string,
  newBindingId: string,
  sheetId: string,
  rangeAddr: string
) =>
  Excel.run(async (ctx) => {
    const binding = ctx.workbook.bindings.getItemOrNullObject(bindingId)
    binding.load(['id', 'isNullObject'])
    // sync could fail because of invalid binding
    const syncResult = await ResultAsync.fromPromise(ctx.sync(), (error) =>
      error instanceof Error ? JSON.stringify(error) : error
    )
    syncResult.mapErr((error) =>
      console.error(`recreateBinding sync error, error: ${error}`)
    )

    if (binding.id) {
      const releaseBindingResult = await ResultAsync.fromPromise(
        releaseBindingIdAsync(binding.id),
        (error) => (error instanceof Error ? JSON.stringify(error) : error)
      )

      releaseBindingResult.mapErr((error) =>
        console.error(`recreateBinding release binding error, error: ${error}`)
      )
    }

    await ctx.sync()

    const sheet = ctx.workbook.worksheets.getItem(sheetId)
    const range = sheet.getRange(rangeAddr)
    const createExcelBindingResult = await createExcelBinding({
      ctx,
      id: newBindingId,
      range,
      type: Excel.BindingType.range,
      onDataChanged: () => {
        publishBindingOnDataChangeEvent(newBindingId)
      },
      onSelectionChanged: () => {
        publishOnReferenceSelectedEvent(newBindingId)
      },
    })

    if (createExcelBindingResult.isErr()) {
      throw createExcelBindingResult.error
    }

    await ctx.sync()
  })

export const isEmptyRange = (bindingId: string) =>
  Excel.run(async (ctx) => {
    const binding = ctx.workbook.bindings.getItemOrNullObject(bindingId)
    binding.load(['isNullObject'])
    await ctx.sync()
    if (binding.isNullObject) {
      return true
    }

    const range = binding.getRange()
    range.load(['values'])
    await ctx.sync()
    return range.values.every((row) => row.every((value) => value === ''))
  })
export const getColumnIndex = (address: string, firstRowHeader: 0 | 1) =>
  Excel.run(async (ctx) => {
    if (address === '') return
    const sheet = ctx.workbook.worksheets.getActiveWorksheet()
    const range = sheet.getRange(address)
    range.load(['columnCount', 'columnIndex', 'rowIndex'])
    await ctx.sync()
    const columns = []
    for (
      let i = range.columnIndex;
      i < range.columnIndex + range.columnCount;
      i++
    ) {
      columns.push(numberToLetters(i))
    }
    if (!firstRowHeader) return columns

    const ranges: Excel.Range[] = []

    for (
      let i = range.columnIndex;
      i < range.columnIndex + range.columnCount;
      i++
    ) {
      const r = sheet.getRangeByIndexes(range.rowIndex, i, 1, 1)
      r.load('values')
      ranges.push(r)
    }

    await ctx.sync()
    return columns.map((val, i) => `${val} | ${ranges[i].values[0][0]}`)
  })

export const clearBindingForDataMatch = async (
  ctx: Excel.RequestContext,
  sheet: Excel.Worksheet,
  rowIndex: number,
  rowCount: number,
  outputRange: string[]
) => {
  if (rowCount <= 0) return
  const formattedRanges = outputRange.map((col) => {
    if (col === NO_OUTPUT) return null
    if (rowCount === 1) return `${col}${rowIndex + 1}`
    else return `${col}${rowIndex + 1}:${col}${rowIndex + 1 + rowCount}`
  })
  const filteredFormattedRanges = formattedRanges.filter(
    (range) => range !== null
  )
  const selectedRanges = sheet.getRanges(filteredFormattedRanges.join(', '))
  sheet.load('id')
  ctx.workbook.bindings.load('items')
  await ctx.sync()
  const bindings = ctx.workbook.bindings.items
  const ranges = bindings.map((b) => b.getRange())
  ranges.forEach((range) => range.worksheet.load('id'))
  await ctx.sync()
  const filteredRanges = ranges.filter(
    (range) => range.worksheet.id === sheet.id
  )
  const bindingIntersections = filteredRanges.map((range) =>
    selectedRanges.getIntersectionOrNullObject(range)
  )
  bindingIntersections.forEach((intersection) =>
    intersection.load('isNullObject')
  )
  await ctx.sync()
  for (let i = 0; i < bindingIntersections.length; i++) {
    if (!bindingIntersections[i].isNullObject) {
      console.log('DELETED!!!')
      bindings[i].delete()
    }
  }
  await ctx.sync()
}

export const formatTable = (table: Excel.Range, values: string[][]) => {
  // Apply number format based on whether values are large numbers
  const numberFormats = values.map(row =>
    row.map(value => isLargeNumber(value) ? '@' : 'General')
  )
  table.numberFormat = numberFormats
  table.values = values
  table.format.borders.getItem('EdgeBottom').style = 'Continuous'
  table.format.borders.getItem('EdgeLeft').style = 'Continuous'
  table.format.borders.getItem('EdgeRight').style = 'Continuous'
  table.format.borders.getItem('EdgeTop').style = 'Continuous'
  table.format.autofitColumns()
  table.format.autofitRows()
  table.format.font.name = 'Segoe UI'
  table.format.fill.color = '#DBFFD1'
}

export const enableEvents = () =>
  Excel.run(async (ctx) => {
    ctx.runtime.enableEvents = true
    await ctx.sync()
  })

export const disableEvents = async (ctx: Excel.RequestContext) => {
  ctx.runtime.enableEvents = false
  await ctx.sync()
}

export const cacheLocalFile = async (file: CompressedFile) => {
  const decompressedOCR = decompressFromUTF16(file.compressedOCR)
  const ocr: TestDataOcr = JSON.parse(decompressedOCR)
  const decompressedPDF = decompressFromUTF16(file.compressedPDF)
  const pdfBlob = base64ToBlob(decompressedPDF, 'application/pdf')
  const [, , , lines, words] = breakDownOcr(ocr, 0)
  const blocks = [...lines.values(), ...words.values()]
  const dateBlocks = getDateBlocks(blocks)
  await addFileToDB({
    id: file.fileId,
    type: file.type,
    folderName: file.folderName,
    parentFolderId: file.parentFolderId,
    createdAt: new Date(),
    extractionMethod: file.extractionMethod,
    fileName: file.fileName,
    ocr,
    ocrDateCache: dateBlocks,
    pdf: pdfBlob,
    extractionEngine: file.extractionEngine,
  })
}

export const getColumnSize = (range: string) =>
  Excel.run(async (ctx) => {
    try {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet()
      const r = sheet.getRange(range)
      r.load(['isNullObject', 'columnCount'])
      await ctx.sync()
      if (r.isNullObject) return 0
      return r.columnCount
    } catch (error) {
      return 0
    }
  })

export const getCurrentSheetAndRange = async (ctx: Excel.RequestContext) => {
  const sheet = ctx.workbook.worksheets.getActiveWorksheet()
  const range = ctx.workbook.getSelectedRange()
  sheet.load(['id', 'name'])
  range.load(['rowIndex', 'columnIndex', 'rowCount'])
  await ctx.sync()
  return { sheet, range, ctx }
}

export const getCell = async (
  ctx: Excel.RequestContext,
  worksheet: Excel.Worksheet,
  row: number,
  col: number
) => {
  const cell = worksheet.getCell(row, col)
  cell.load(['address', 'values'])
  await ctx.sync()
  return { cell, ctx }
}

export const formatTextSnipCell = async (
  cell: Excel.Range,
  value: any,
  font: string = 'Segoe UI',
  color: string = '#CCFCFC'
) => {
  if (isLargeNumber(value)) {
    cell.numberFormat = [['@']]
  }
  cell.values = [[value]]
  cell.format.font.name = font
  cell.format.fill.color = color
  cell.format.autofitColumns()
  cell.format.horizontalAlignment = 'Right'
}

export const exportTemplateDataToWorksheet = (drawings: TemplateDrawing[]) =>
  Excel.run(async (ctx) => {
    const settings = localStorage.getItem(USER_SETTINGS)
    const userSettings = settings
      ? (JSON.parse(settings) as UserSettings)
      : null
    const { range, sheet } = await getCurrentSheetAndRange(ctx)
    const nextRange = sheet.getRangeByIndexes(
      range.rowIndex + 1,
      range.columnIndex,
      1,
      1
    )
    nextRange.load(['rowIndex', 'columnIndex'])
    await ctx.sync()

    const headerRanges = drawings.map((_, i) =>
      sheet.getRangeByIndexes(range.rowIndex, range.columnIndex + i, 1, 1)
    )

    const ranges = drawings.map((drawing, i) =>
      drawing.valueRects.map((_, j) =>
        sheet.getRangeByIndexes(
          nextRange.rowIndex + j,
          nextRange.columnIndex + i,
          1,
          1
        )
      )
    )

    headerRanges.forEach((r) => r.load(['values']))

    ranges.forEach((r) =>
      r.forEach((range) =>
        range.load([
          'address',
          'values',
          'rowIndex',
          'rowCount',
          'columnIndex',
          'rowIndex',
        ])
      )
    )
    await ctx.sync()

    headerRanges.forEach((r, i) => {
      r.values = [[drawings[i].name]]
      r.format.autofitColumns()
      r.format.fill.color = '#CCFCFC'
      r.format.horizontalAlignment = 'Right'
    })

    const elems: TextcutRectangle[] = []
    for (let i = 0; i < drawings.length; i++) {
      const rectangles = drawings[i].valueRects
      for (let j = 0; j < rectangles.length; j++) {
        const rectangle = rectangles[j]
        if (!rectangle || !rectangle.fileId) continue
        const nano = nanoid()
        const top = rectangle.y / rectangle.ocrH
        const { fill, stroke } = getCutTypeStyle(Cut.TEXTCUT)
        const textDate = extractAndParseDate(
          rectangle.value,
          userSettings ? userSettings.dateFormat : DateFormat.US
        )
        const validDate = isValidDate(
          rectangle.value,
          userSettings ? userSettings.dateFormat : DateFormat.US
        )
        const numericValue = isLargeNumber(rectangle.value) ? rectangle.value : (isCurrencyOrNumericOnly(rectangle.value) ? getNumericValue(rectangle.value) : rectangle.value)
        const elem = createTextcutRectangle({
          cellColor: '#CCFCFC',
          fileId: rectangle.fileId,
          filePage: rectangle.filePage,
          fill,
          stroke,
          x: rectangle.x,
          y: rectangle.y,
          w: rectangle.w,
          h: rectangle.h,
          ocrH: rectangle.ocrH,
          ocrW: rectangle.ocrW,
          range: ranges[i][j],
          rectId: nano,
          rotationDegree: 0,
          sheetId: sheet.id,
          sheetName: sheet.name,
          tag: CutTag.TEXTCUT,
          top,
          value: validDate.valid && textDate ? textDate : (numericValue ? numericValue : rectangle.value),
        })
        if (elem) {
          elems.push(elem)
          formatTextSnipCell(
            ranges[i][j],
            validDate.valid && textDate ? textDate : (numericValue ? numericValue : rectangle.value)
          )
          if (elem.values[0][0]) {
            const createBindingResult = await createExcelBinding({
              ctx,
              id: nano,
              range: ranges[i][j],
              type: Excel.BindingType.range,
              onDataChanged: () => {
                publishBindingOnDataChangeEvent(nano)
              },
              onSelectionChanged: () => {
                publishOnReferenceSelectedEvent(nano)
              },
            })

            if (createBindingResult.isErr()) {
              throw createBindingResult.error
            }
          }
        }
        if (
          i === drawings.length - 1 &&
          j === drawings[i].valueRects.length - 1
        ) {
          const nextRange = sheet.getRangeByIndexes(
            ranges[i][j].rowIndex + ranges[i][j].rowCount,
            ranges[i][j].columnIndex,
            1,
            1
          )
          nextRange.select()
        }
      }
    }
    await ctx.sync()
    return elems.filter((elem) => elem.values[0][0])
  })

export const getRange = async (
  ctx: Excel.RequestContext,
  sheetId?: string,
  rangeAddr?: string
) => {
  let sheet: Excel.Worksheet | null = null
  if (sheetId) {
    sheet = ctx.workbook.worksheets.getItemOrNullObject(sheetId)
    sheet.load('isNullObject')
    await ctx.sync()
  }
  if (!sheet || sheet.isNullObject) {
    sheet = ctx.workbook.worksheets.getActiveWorksheet()
  }
  if (!sheet) {
    throw new Error('Failed to get a worksheet')
  }

  let range: Excel.Range | null = null

  if (rangeAddr) {
    range = sheet.getRange(rangeAddr)
    range.load('isNullObject')
    await ctx.sync()
  }

  if (!range || range.isNullObject) {
    range = ctx.workbook.getSelectedRange()
  }
  if (!range) {
    throw new Error('Failed to get a range')
  }
  sheet.load(['id', 'name'])
  range.load(['rowIndex', 'columnIndex', 'rowCount', 'columnCount'])

  await ctx.sync()

  return { sheet, range }
}

type CutTextToWorksheetArg = {
  text: string
  fileId: string
  filePage: number
  rect: Rectangle
  ratioX: numeral.Numeral
  ratioY: numeral.Numeral
  ocrPageH: number
  ocrPageW: number
  rotationDegree: number
  sheetId?: string
  rangeAddr?: string
  nextCell: NextCell
  dateFormat: DateFormat
}

export const cutTextToWorksheet = ({
  fileId,
  filePage,
  text,
  rect,
  ratioX,
  ratioY,
  ocrPageH,
  ocrPageW,
  rotationDegree,
  rangeAddr,
  sheetId,
  nextCell,
  dateFormat,
}: CutTextToWorksheetArg) =>
  Excel.run(async (ctx) => {
    const outputText = parseCell(text, dateFormat)
    const { range, sheet } = await getRange(ctx, sheetId, rangeAddr)
    const nano = nanoid()
    const { cell: firstCell } = await getCell(
      ctx,
      sheet,
      range.rowIndex,
      range.columnIndex
    )
    const top =
      rect?.y && ratioY.value()
        ? ((rect.y * ratioY.value()!) / ocrPageH) * 100
        : 0
    const { fill, stroke } = getCutTypeStyle(Cut.TEXTCUT)
    const elem = createTextcutRectangle({
      cellColor: '#CCFCFC',
      fileId,
      filePage,
      fill,
      stroke,
      x: rect.x * (ratioX.value() ?? 1),
      y: rect.y * (ratioY.value() ?? 1),
      w: rect.w * (ratioX.value() ?? 1),
      h: rect.h * (ratioY.value() ?? 1),
      ocrH: ocrPageH,
      ocrW: ocrPageW,
      range: firstCell,
      rectId: nano,
      rotationDegree,
      sheetId: sheet.id,
      sheetName: sheet.name,
      tag: CutTag.TEXTCUT,
      top,
      value: outputText,
    })
    let nextRange: null | Excel.Range = null
    if (nextCell === NextCell.RIGHT) {
      nextRange = sheet.getRangeByIndexes(
        range.rowIndex,
        range.columnIndex + range.columnCount,
        1,
        1
      )
    } else {
      nextRange = sheet.getRangeByIndexes(
        range.rowIndex + range.rowCount,
        range.columnIndex,
        1,
        1
      )
    }

    if (elem) {
      const preValues = firstCell.values
      if (isLargeNumber(outputText as string)) {
        firstCell.numberFormat = [['@']]
      }
      formatTextSnipCell(firstCell, outputText)
      const creatBindingResult = await createExcelBinding({
        ctx,
        id: nano,
        range: firstCell,
        type: Excel.BindingType.range,
        onDataChanged: () => {
          publishBindingOnDataChangeEvent(nano)
        },
        onSelectionChanged: () => {
          publishOnReferenceSelectedEvent(nano)
        },
      })

      if (creatBindingResult.isErr()) {
        throw creatBindingResult.error
      }

      nextRange.select()
      await ctx.sync()
      return {
        elem,
        preValues,
      }
    }
    return null
  })

type CutSumToWorksheetArg = {
  isHoldingCtrl: boolean
  sumRects: TextcutRectangle[]
  fileId: string
  filePage: number
  text: string
  rect: Rectangle
  ratioX: numeral.Numeral
  ratioY: numeral.Numeral
  rotationDegree: number
  ocrPageH: number
  ocrPageW: number
  sheetId?: string
  rangeAddress?: string
  nextCell: NextCell
}
export const cutSumToWorksheet = ({
  isHoldingCtrl,
  sumRects,
  filePage,
  text,
  rect,
  ratioX,
  ratioY,
  fileId,
  rotationDegree,
  ocrPageH,
  ocrPageW,
  rangeAddress,
  sheetId,
  nextCell,
}: CutSumToWorksheetArg) =>
  Excel.run(async (ctx) => {
    const groupId = nanoid()
    const { range, sheet } = await getRange(ctx, sheetId, rangeAddress)
    const firstCell = sheet.getCell(range.rowIndex, range.columnIndex)
    const nano = nanoid()
    range.load(['rowIndex', 'columnIndex', 'rowCount', 'columnCount'])
    sheet.load('id')
    firstCell.load(['address', 'values', 'formulas'])
    await ctx.sync()
    const [, rangeAddr] = firstCell.address.split('!')
    let preSumElem: TextcutRectangle | undefined
    if (isHoldingCtrl) {
      preSumElem = sumRects.find(
        (rect) =>
          rect.tag === CutTag.SUM &&
          rect.sheetId === sheet.id &&
          rect.filePage === filePage &&
          rect.rangeAddr === rangeAddr
      )
      if (preSumElem) {
        text = firstCell.formulas + '+' + text.slice(1)
        if (!text.startsWith('=')) text = '=' + text
      }
    }

    const elem = new TextcutRectangleBuilder()
      .addBindingId(nano)
      .addBoudingInfo({
        x: rect.x * (ratioX.value() ?? 1),
        y: rect.y * (ratioY.value() ?? 1),
        h: rect.h * (ratioY.value() ?? 1),
        w: rect.w * (ratioX.value() ?? 1),
      })
      .addFileInfo({ fileId, filePage })
      .addSheetInfo({
        sheetId: sheet.id,
        sheetName: sheet.name,
        range: rangeAddr,
      })
      .addOcrInfo({
        id: '',
        height:
          rotationDegree === 0 || rotationDegree === 180 ? ocrPageH : ocrPageW,
        width:
          rotationDegree === 0 || rotationDegree === 180 ? ocrPageW : ocrPageH,
      })
      .addTag(CutTag.SUM)
      .addStyle({ cellColor: '#FEEECC', ...getCutTypeStyle(Cut.SUM) })
      .addValues([[text]])
      .addDegee(rotationDegree)
      .addBindingGroupId(preSumElem ? preSumElem.bindingGroupId! : groupId)
      .build()
    let nextRange: Excel.Range
    if (nextCell === NextCell.RIGHT) {
      nextRange = sheet.getRangeByIndexes(
        range.rowIndex,
        range.columnIndex + range.columnCount,
        1,
        1
      )
    } else {
      nextRange = sheet.getRangeByIndexes(
        range.rowIndex + range.rowCount,
        range.columnIndex,
        1,
        1
      )
    }
    if (elem) {
      const deleteIds: string[] = []
      if (!preSumElem) {
        const filtered = sumRects.filter(
          (rect) => rect.sheetId === sheet.id && rect.rangeAddr === rangeAddr
        )
        deleteIds.push(...filtered.map((rect) => rect.bindingId))
      }
      if (isHoldingCtrl) {
        firstCell.select()
      } else {
        nextRange.select()
      }
      console.log('text:', text)
      firstCell.values = [[text]]
      const preValues = firstCell.values
      if (isLargeNumber(text)) {
        firstCell.numberFormat = [['0']];
      }
      firstCell.format.font.name = 'Segoe UI'
      firstCell.format.fill.color = '#FEEECC'
      firstCell.format.autofitColumns()
      const createBindingResult = await createExcelBinding({
        ctx,
        id: nano,
        range: firstCell,
        type: Excel.BindingType.range,
        onDataChanged: () => {
          publishBindingOnDataChangeEvent(nano)
        },
        onSelectionChanged: () => {
          publishOnReferenceSelectedEvent(nano)
        },
      })

      if (createBindingResult.isErr()) {
        throw createBindingResult.error
      }

      await ctx.sync()
      return { deleteIds, elem, preValues }
    }
    return null
  })

type CutToWorksheetArg = {
  fileId: string
  filePage: number
  rect: Rectangle
  ratioX: numeral.Numeral
  ratioY: numeral.Numeral
  ocrPageH: number
  ocrPageW: number
  rotationDegree: number
  rangeAddress?: string
  sheetId?: string
  nextCell: NextCell
}
const cutToWorksheet = (
  {
    fileId,
    filePage,
    ocrPageH,
    ocrPageW,
    ratioX,
    ratioY,
    rect,
    rotationDegree,
    rangeAddress,
    sheetId,
    nextCell,
  }: CutToWorksheetArg,
  cut: Cut
) =>
  Excel.run(async (ctx) => {
    const { range, sheet } = await getRange(ctx, sheetId, rangeAddress)
    const firstCell = sheet.getCell(range.rowIndex, range.columnIndex)
    const nano = nanoid()
    range.load(['rowIndex', 'columnIndex', 'rowCount', 'columnCount'])
    sheet.load('id')
    firstCell.load(['address', 'values'])
    await ctx.sync()
    const [, rangeAddr] = firstCell.address.split('!')
    const top =
      rect?.y && ratioY.value()
        ? ((rect.y * ratioY.value()!) / ocrPageH) * 100
        : 0
    const elem = new TextcutRectangleBuilder()
      .addBindingId(nano)
      .addBoudingInfo({
        x: rect.x * (ratioX.value() ?? 1),
        y: rect.y * (ratioY.value() ?? 1),
        h: rect.h * (ratioY.value() ?? 1),
        w: rect.w * (ratioX.value() ?? 1),
      })
      .addFileInfo({ fileId, filePage })
      .addSheetInfo({
        sheetId: sheet.id,
        sheetName: sheet.name,
        range: rangeAddr,
      })
      .addOcrInfo({
        id: '',
        height:
          rotationDegree === 0 || rotationDegree === 180 ? ocrPageH : ocrPageW,
        width:
          rotationDegree === 0 || rotationDegree === 180 ? ocrPageW : ocrPageH,
      })
      .addTag(cut === Cut.VALIDATE ? CutTag.VALIDATE : CutTag.EXCEPTION)
      .addStyle({ cellColor: '#CCFCFC', ...getCutTypeStyle(cut) })
      .addValues([[`${cut === Cut.VALIDATE ? '\u2714' : '\u00D7'}`]])
      .addDegee(rotationDegree)
      .addTop(top)
      .build()
    let nextRange: Excel.Range
    if (nextCell === NextCell.RIGHT) {
      nextRange = sheet.getRangeByIndexes(
        range.rowIndex,
        range.columnIndex + range.columnCount,
        1,
        1
      )
    } else {
      nextRange = sheet.getRangeByIndexes(
        range.rowIndex + range.rowCount,
        range.columnIndex,
        1,
        1
      )
    }
    nextRange.select()
    if (elem) {
      const preValues = firstCell.values
      firstCell.values = [[`${cut === Cut.VALIDATE ? '\u2714' : '\u00D7'}`]]
      firstCell.format.font.name = 'Segoe UI'
      firstCell.format.fill.color = cut === Cut.VALIDATE ? '#CCEBE0' : '#FBD0D7'
      firstCell.format.autofitColumns()
      firstCell.format.font.color = cut === Cut.VALIDATE ? '#00734E' : '#9C0006'
      firstCell.format.verticalAlignment = 'Center'
      firstCell.format.horizontalAlignment = 'Center'
      const createBindingResult = await createExcelBinding({
        ctx,
        id: nano,
        range: firstCell,
        type: Excel.BindingType.range,
        onDataChanged: () => {
          publishBindingOnDataChangeEvent(nano)
        },
        onSelectionChanged: () => {
          publishOnReferenceSelectedEvent(nano)
        },
      })
      if (createBindingResult.isErr()) {
        throw createBindingResult.error
      }
      await ctx.sync()
      return { elem, preValues }
    }
    return null
  })

export const cutValidateToWorksheet = (arg: CutToWorksheetArg) =>
  cutToWorksheet(arg, Cut.VALIDATE)

export const cutExceptionToWorksheet = (arg: CutToWorksheetArg) =>
  cutToWorksheet(arg, Cut.EXCEPTION)

type CutTableToWorksheetArg = {
  fileId: string
  filePage: number
  matrix: any[][]
  x1: number
  y1: number
  width: number
  height: number
  ratioY: numeral.Numeral
  ocrH: number
  ocrW: number
  rotationDegree: number
  lines: UserGeneratedLine[]
  sheetId?: string
  rangeAddress?: string
  callback?: () => Promise<void>
  parseTableBackend?: boolean
  nextCell: NextCell
  dateFormat: DateFormat
}
export const cutTableToWorksheet = ({
  matrix,
  x1,
  y1,
  height,
  width,
  ratioY,
  fileId,
  filePage,
  ocrH,
  ocrW,
  lines,
  rotationDegree,
  rangeAddress,
  sheetId,
  callback,
  parseTableBackend = false,
  nextCell,
  dateFormat,
}: CutTableToWorksheetArg) =>
  Excel.run(async (ctx) => {
    const { range, sheet } = await getRange(ctx, sheetId, rangeAddress)

    // calculate the range, take range as starting point
    const tableRowCount = matrix.length
    const tableColCount = matrix[0].length

    const startCol = range.columnIndex
    const startRow = range.rowIndex
    const endCol = startCol + tableColCount - 1
    const endRow = startRow + tableRowCount - 1

    const matrixRange = `${numberToLetters(startCol)}${
      startRow + 1
    }:${numberToLetters(endCol)}${endRow + 1}`
    const outputRange = ctx.workbook.worksheets
      .getActiveWorksheet()
      .getRange(matrixRange)
    outputRange.load([
      'rowIndex',
      'columnIndex',
      'rowCount',
      'columnCount',
      'address',
      'values',
    ])

    await ctx.sync()
    const preValues = outputRange.values
    const parsedMatrix = matrix.map((row) =>
      row.map((item) => parseCell(item, dateFormat))
    )
    const transposedMatrix = parsedMatrix
    formatTable(outputRange, transposedMatrix as string[][])
    const nano = nanoid()
    const vs = [
      { x: x1, y: y1 },
      { x: x1 + width, y: y1 },
      { x: x1 + width, y: y1 + height },
      { x: x1, y: y1 + height },
    ]
    const topLeft = findTOpLeftPointFromVertices(vs)
    const [h, w] = calculateHeightandWidth(vs[0], vs[1], vs[2], vs[3])
    const [, rangeAddr] = outputRange.address.split('!')

    let top = 0

    if (parseTableBackend) {
      top = topLeft ? (topLeft.y / ocrH) * 100 : 0
    } else {
      top =
        topLeft?.y && ratioY.value()
          ? ((topLeft.y * ratioY.value()!) / ocrH) * 100
          : 0
    }
    const elem = new TextcutRectangleBuilder()
      .addBoudingInfo({
        x: topLeft?.x ?? 0,
        y: topLeft?.y ?? 0,
        h,
        w,
      })
      .addSheetInfo({
        range: rangeAddr,
        sheetId: sheet.id,
        sheetName: sheet.name,
      })
      .addFileInfo({ fileId, filePage })
      .addOcrInfo({ height: ocrH, id: '', width: ocrW })
      .addStyle({
        cellColor: '#DBFFD1',
        fill: 'rgba(219, 255, 209, 0.3)',
        stroke: 'green',
      })
      .addBindingId(nano)
      .addDegee(rotationDegree)
      .addTag(CutTag.TABLE)
      .addValues(transposedMatrix)
      .addLines(lines)
      .addTop(top)
      .build()
    if (nextCell === NextCell.RIGHT) {
      const nextRange = sheet.getRangeByIndexes(
        outputRange.rowIndex,
        outputRange.columnIndex + outputRange.columnCount,
        1,
        1
      )
      nextRange.select()
    } else {
      const nextRange = sheet.getRangeByIndexes(
        outputRange.rowIndex + outputRange.rowCount,
        outputRange.columnIndex,
        1,
        1
      )
      nextRange.select()
    }
    if (elem) {
      if (callback) await callback()
      const createBindingResult = await createExcelBinding({
        ctx,
        id: nano,
        range: outputRange,
        type: Excel.BindingType.range,
        onDataChanged: () => {
          publishBindingOnDataChangeEvent(nano)
        },
        onSelectionChanged: () => {
          publishOnReferenceSelectedEvent(nano)
        },
      })
      if (createBindingResult.isErr()) {
        throw createBindingResult.error
      }
      return { elem, preValues }
    }
    return null
  })

type CreateRedactionArg = {
  fileId: string
  filePage: number
  rect: Rectangle
  ratioX: numeral.Numeral
  ratioY: numeral.Numeral
  rotationDegree: number
  ocrPageH: number
  ocrPageW: number
}
export const createRedaction = ({
  rect,
  ratioX,
  ratioY,
  fileId,
  filePage,
  rotationDegree,
  ocrPageH,
  ocrPageW,
}: CreateRedactionArg) => {
  const nano = nanoid()
  const elem = new TextcutRectangleBuilder()
    .addBindingId(nano)
    .addBoudingInfo({
      x: rect.x * (ratioX.value() ?? 1),
      y: rect.y * (ratioY.value() ?? 1),
      h: rect.h * (ratioY.value() ?? 1),
      w: rect.w * (ratioX.value() ?? 1),
    })
    .addFileInfo({ fileId, filePage })
    .addSheetInfo({
      sheetId: '',
      sheetName: '',
      range: '',
    })
    .addOcrInfo({
      id: '',
      height:
        rotationDegree === 0 || rotationDegree === 180 ? ocrPageH : ocrPageW,
      width:
        rotationDegree === 0 || rotationDegree === 180 ? ocrPageW : ocrPageH,
    })
    .addTag(CutTag.REDACT)
    .addStyle({ cellColor: '', ...getCutTypeStyle(Cut.REDACT) })
    .addValues([])
    .addDegee(rotationDegree)
    .build()

  return elem
}

export const updateLocalPDF = async (
  id: string,
  data: Partial<CompressedFile>
) =>
  Excel.run(async (ctx) => {
    const settings = ctx.workbook.settings
    settings.load('items')
    await ctx.sync()
    const items = settings.items
    const settingArray: Excel.Setting[] = []
    for (const setting of items) {
      settingArray.push(setting)
      setting.load(['value', 'key', 'isNullObject'])
    }
    await ctx.sync()
    const filteredSettingArray = settingArray.filter(
      (setting) => !setting.isNullObject && setting.key.startsWith('FILE_')
    )
    for (const item of filteredSettingArray) {
      const key = item.key
      const value = item.value
      const [, fileId] = key.split('_')

      if (fileId !== id) continue
      const parsedValue: CompressedFile = JSON.parse(value)
      const newParsedValue = {
        ...parsedValue,
        ...data,
      }
      const newValue = JSON.stringify(newParsedValue)
      settings.add(key, newValue)
      break
    }
    await ctx.sync()
  })

/**
 * Parses a cell value and converts it to the most appropriate data type.
 * This function intelligently determines whether a string represents a date,
 * numeric/currency value, or should remain as text.
 *
 * Processing logic:
 * 1. **Date parsing**: If the value contains only date components, attempts to parse as a date
 * 2. **Numeric parsing**: If the value contains only currency/numeric components, extracts the numeric value
 * 3. **Text fallback**: Returns the trimmed string if no other parsing is successful
 *
 * Date handling:
 * - Uses `isDateOnly()` to check if string contains only date components (no additional text)
 * - Validates date using `isValidDate()` with the specified locale format
 * - Extracts and formats date using `extractAndParseDate()` if valid
 *
 * Numeric/Currency handling:
 * - Uses `isCurrencyOrNumericOnly()` to check if string contains only monetary/numeric components
 * - Extracts numeric value using `extractNumericValueFromAmount()` which handles various formats:
 *   - Currency symbols: $, €, £, ¥, etc.
 *   - Thousands separators: 1,234.56, 1.234,56, 1'234'567.89
 *   - Negative formats: ($100), -$100, (100.00)
 *
 * @param value - The cell value to parse (typically a string from Excel)
 * @param dateFormat - The date format/locale to use for date parsing (e.g., DateFormat.AU, DateFormat.US)
 * @returns The parsed value as Date string (YYYY-MM-DD format), number, or original string
 *
 * @example
 * ```typescript
 * parseCell("2025-01-01", DateFormat.AU)     // returns "2025-01-01"
 * parseCell("01/15/2025", DateFormat.US)     // returns "2025-01-15"
 * parseCell("$1,234.56", DateFormat.US)      // returns 1234.56
 * parseCell("€ 1.234,67", DateFormat.AU)     // returns 1234.67
 * parseCell("(123.45)", DateFormat.US)       // returns -123.45
 * parseCell("Price: $100", DateFormat.US)    // returns "Price: $100" (unchanged)
 * parseCell("Hello World", DateFormat.US)    // returns "Hello World" (unchanged)
 * parseCell("  text  ", DateFormat.US)       // returns "text" (trimmed)
 * ```
 */
export function parseCell(
  value: string,
  dateFormat: DateFormat
): Date | number | string {
  if (typeof value !== 'string') return value

  const trimmedValue = value.trim().replace(/\s+/g, ' ').toLocaleLowerCase()

  if (isDateOnly(trimmedValue)) {
    // Check if it's a valid date
    const validDate = isValidDate(trimmedValue, dateFormat)
    if (validDate.valid) {
      const parsedDate = extractAndParseDate(trimmedValue, dateFormat)
      if (parsedDate) return parsedDate
    }
  }

  if (isCurrencyOrNumericOnly(trimmedValue)) {
    return getNumericValue(trimmedValue)
  }

  return value.trim()
}

/**
 * Retrieves the address of the currently selected range in the active Excel workbook.
 *
 * This function uses the Excel JavaScript API to access the selected range and returns its address.
 * It ensures that the range's address property is loaded and synchronized before returning the value.
 *
 * @returns {Promise<string>} A promise that resolves to the address of the selected range in the workbook.
 *
 * @example
 * getActiveRange().then(address => {
 *   console.log(`Selected range address: ${address}`);
 * });
 *
 * @throws {Error} If there is an issue with the Excel context or API execution.
 */
export const getActiveRange = (): Promise<string> =>
  Excel.run(async (ctx) => {
    const range = ctx.workbook.getSelectedRange()
    range.load(['address'])
    await ctx.sync()
    return range.address
  })

/*
 * Retrieves the active worksheet in the Excel workbook.
 *
 * @param ctx - The Excel request context to use for the operation.
 * @param properties - Optional array of property names to load from the worksheet.
 * Default is an empty array, meaning no properties are explicitly loaded.
 *
 * @returns A ResultAsync containing the active Excel.Worksheet if successful,
 * or an Error with a descriptive message if the operation fails.
 *
 * @example
 * ```typescript
 * // Within an Excel.run context
 * await Excel.run(async (ctx) => {
 *   const worksheetResult = await getActiveWorksheet(ctx, ['name', 'id']);
 *   if (worksheetResult.isOk()) {
 *     const worksheet = worksheetResult.value;
 *     // ... use the worksheet
 *   }
 * });
 * ```
 */
export const getActiveWorksheet = (
  ctx: Excel.RequestContext,
  properties: string[] = []
): ResultAsync<Excel.Worksheet, Error> =>
  ResultAsync.fromPromise(
    (async () => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet()
      if (properties.length > 0) {
        sheet.load(properties)
        await ctx.sync()
      }
      return sheet
    })(),
    (error) =>
      new Error(`Failed to get active worksheet: ${errorStringify(error)}`)
  )

/**
 * Retrieves an Excel range from a given worksheet based on a range address.
 *
 * @param ctx - The Excel request context to use for the operation.
 * @param sheet - The Excel worksheet containing the range.
 * @param rangeAddr - The address of the range to retrieve (e.g., "A1:B10").
 * @param properties - Optional array of range properties to load. If not provided, no properties are loaded.
 * @returns A ResultAsync containing the Excel Range object if successful, or an Error if the operation fails.
 *
 * @example
 * ```typescript
 * // Within an Excel.run context
 * await Excel.run(async (ctx) => {
 *   const worksheetResult = await getActiveWorksheet(ctx, ['name']);
 *   if (worksheetResult.isOk()) {
 *     const rangeResult = await getRangeByWorksheet(ctx, worksheetResult.value, "A1:B10", ["address", "values"]);
 *     if (rangeResult.isOk()) {
 *       console.log('Range address:', rangeResult.value.address);
 *     }
 *   }
 * });
 * ```
 */
export const getRangeByWorksheet = (
  ctx: Excel.RequestContext,
  sheet: Excel.Worksheet,
  rangeAddr: string,
  properties: string[] = []
) =>
  ResultAsync.fromPromise(
    (async () => {
      const range = sheet.getRange(rangeAddr)
      if (properties.length > 0) {
        range.load(properties)
        await ctx.sync()
      }
      return range
    })(),
    (error) =>
      new Error(`Failed to get range by worksheet: ${errorStringify(error)}`)
  )

/**
 * Gets the output ranges that match data based on the provided input and formatting.
 *
 * @param ctx - Excel request context used for API calls
 * @param formattedResult - 2D array containing the formatted data results
 * @param filteredOutputRange - Array of column letters representing the output range
 * @param inputRange - Excel Range object representing the source input range
 * @param worksheet - Excel Worksheet object where the ranges are located
 * @returns A Result containing a 2D array of Excel.Range objects corresponding to the output ranges,
 *          or an Error if the operation fails
 */
export const getDataMatchOutputRanges = (
  ctx: Excel.RequestContext,
  formattedResult: any[][],
  filteredOutputRange: string[],
  inputRange: Excel.Range,
  worksheet: Excel.Worksheet
) =>
  ResultAsync.fromPromise(
    (async () => {
      const outputRanges: Excel.Range[][] = []

      for (let i = 0; i < formattedResult.length; i++) {
        const row: Excel.Range[] = []
        for (let j = 0; j < filteredOutputRange.length; j++) {
          const letter = lettersToNumber(filteredOutputRange[j])
          const range = worksheet.getRangeByIndexes(
            inputRange.rowIndex + i,
            letter - 1,
            1,
            1
          )
          range.load(['address', 'rowIndex', 'columnIndex', 'values'])
          row.push(range)
        }
        outputRanges.push(row)
      }
      await ctx.sync()
      return outputRanges
    })(),
    (error) =>
      new Error(
        `Failed to get data match output ranges: ${errorStringify(error)}`
      )
  )

/**
 * Creates an Excel binding for a specified range with optional event handlers.
 *
 * This function wraps the Excel binding creation process in a ResultAsync pattern,
 * providing type-safe error handling and consistent function composition with other
 * Excel operations in the codebase.
 *
 * @param {Object} params - The parameters object.
 * @param {Excel.RequestContext} params.ctx - The Excel request context for API operations.
 * @param {string} params.id - The unique identifier for the binding (must be unique within the workbook).
 * @param {Excel.Range} params.range - The Excel range to bind to.
 * @param {Excel.BindingType} params.type - The type of binding to create ('Range', 'Table', or 'Text').
 * @param {Function} [params.onSelectionChanged] - Optional callback function triggered when the selection changes in the binding.
 * @param {Function} [params.onDataChanged] - Optional callback function triggered when data changes in the binding.
 *
 * @returns {ResultAsync<void, Error>} A ResultAsync that resolves when the binding is created and events are registered,
 *                                     or rejects with an error if the operation fails.
 *
 * @example
 * ```typescript
 * // Within an Excel.run context
 * await Excel.run(async (ctx) => {
 *   const worksheetResult = await getActiveWorksheet(ctx, ['name']);
 *   if (worksheetResult.isOk()) {
 *     const rangeResult = await getRangeByWorksheet(ctx, worksheetResult.value, "A1:B10");
 *     if (rangeResult.isOk()) {
 *       const bindingResult = await createExcelBinding({
 *         ctx,
 *         id: 'myBinding',
 *         range: rangeResult.value,
 *         type: 'Range',
 *         onSelectionChanged: (args) => console.log('Selection changed'),
 *         onDataChanged: (args) => console.log('Data changed')
 *       });
 *
 *       if (bindingResult.isErr()) {
 *         console.error('Failed to create binding:', bindingResult.error);
 *       }
 *     }
 *   }
 * });
 * ```
 */
export const createExcelBinding = ({
  ctx,
  id,
  range,
  type,
  onSelectionChanged,
  onDataChanged,
}: {
  ctx: Excel.RequestContext
  range: Excel.Range
  id: string
  onSelectionChanged?: (...any: any[]) => any
  onDataChanged?: (...any: any[]) => any
  type: Excel.BindingType
}) =>
  ResultAsync.fromPromise(
    (async () => {
      const binding = ctx.workbook.bindings.add(range, type, id)
      binding.onSelectionChanged.add(onSelectionChanged || (() => {}))
      binding.onDataChanged.add(onDataChanged || (() => {}))
      await ctx.sync()
    })(),
    (error) =>
      new Error(`Failed to create Excel binding: ${errorStringify(error)}`)
  )

/**
 * Creates Excel bindings for a collection of TextcutRectangles.
 *
 * This function iterates through an array of TextcutRectangle objects and creates Excel bindings
 * for each unique binding group. If a rectangle belongs to a binding group that has already
 * been processed, it is skipped to avoid duplicate bindings.
 *
 * @param ctx - The Excel RequestContext for the current session
 * @param rects - An array of TextcutRectangle objects to create bindings for
 * @param sheet - The Excel Worksheet where the bindings will be created
 * @returns A Result object that resolves to void if successful, or an Error if binding creation fails
 */
export const createDataMatchBindings = (
  ctx: Excel.RequestContext,
  rects: TextcutRectangle[],
  sheet: Excel.Worksheet
) =>
  ResultAsync.fromPromise(
    (async () => {
      const bindingSet = new Set<string>()
      for (const elem of rects) {
        if (elem.bindingGroupId && bindingSet.has(elem.bindingGroupId)) continue
        const range = sheet.getRange(elem.rangeAddr)
        const createBindingResult = await createExcelBinding({
          id: elem.bindingId,
          ctx,
          range,
          type: Excel.BindingType.range,
          onSelectionChanged: () => {
            publishOnReferenceSelectedEvent(elem.bindingId)
          },
          onDataChanged: () => {
            publishBindingOnDataChangeEvent(elem.bindingId)
          },
        })
        if (createBindingResult.isErr()) {
          throw createBindingResult.error
        }
        if (elem.bindingGroupId) {
          bindingSet.add(elem.bindingGroupId)
        }
      }
      await ctx.sync()
    })(),
    (error) =>
      new Error(
        `Failed to create data match bindings: ${errorStringify(error)}`
      )
  )

/**
 * Removes all bindings from the current Excel workbook.
 *
 * This function performs the following operations:
 * 1. Loads all existing bindings from the workbook
 * 2. Attempts to load binding IDs for each binding
 * 3. If loading IDs fails, releases bindings using their IDs asynchronously
 * 4. If loading IDs succeeds, deletes all bindings directly
 * 5. Synchronizes changes with Excel
 *
 * @returns A ResultAsync that resolves when all bindings are successfully removed,
 * or rejects with an error if the operation fails at any stage
 *
 * @throws {Error} When failing to load binding items or when the final sync operation fails
 *
 * @example
 * ```typescript
 * const result = await removeAllBindings();
 * if (result.isOk()) {
 *   console.log('All bindings removed successfully');
 * } else {
 *   console.error('Failed to remove bindings:', result.error);
 * }
 * ```
 */
export const removeAllBindings = () =>
  ResultAsync.fromPromise(
    Excel.run(async (ctx) => {
      const bindings = ctx.workbook.bindings
      bindings.load('items')
      const bindingItemsResult = await ResultAsync.fromPromise(
        ctx.sync(),
        (error) => errorStringify(error)
      )

      if (bindingItemsResult.isErr()) {
        throw new Error(
          `Failed to load binding items, error: ${errorStringify(
            bindingItemsResult.error
          )}`
        )
      }

      const bindingItems = bindings.items
      bindingItems.forEach((item) => item.load('id'))
      const loadBindingIdResult = await ResultAsync.fromPromise(
        ctx.sync(),
        (error) => errorStringify(error)
      )

      if (loadBindingIdResult.isErr()) {
        for (const item of bindingItems) {
          await ResultAsync.fromPromise(
            releaseBindingIdAsync(item.id),
            () => {}
          )
        }
      } else {
        for (const item of bindingItems) {
          item.delete()
        }
      }

      const lastSyncResult = await ResultAsync.fromPromise(
        ctx.sync(),
        (error) => errorStringify(error)
      )

      if (lastSyncResult.isErr()) {
        throw new Error(
          `removeAllBindings, failed at last sync, error: ${errorStringify(
            lastSyncResult.error
          )}`
        )
      }
    }),
    (error) =>
      new Error(`Faile to remove all bindings, error: ${errorStringify(error)}`)
  )

const getWorksheet = async (ctx: Excel.RequestContext, sheetId: string) => {
  const worksheet = ctx.workbook.worksheets.getItem(sheetId)
  worksheet.load(['id', 'name'])
  await ctx.sync()
  return worksheet
}

export const getWorksheetFromPromise = (
  ctx: Excel.RequestContext,
  sheetId: string
) => fromPromise(getWorksheet(ctx, sheetId), (error) => errorFormatter(error))

export const recreateBindings = () =>
  fromPromise(
    (() =>
      Excel.run(async (ctx) => {
        const settings = ctx.workbook.settings
        const item = settings.getItemOrNullObject('REFERENCES')
        item.load(['isNullObject', 'value'])
        let syncResult = await ResultAsync.fromPromise(ctx.sync(), (error) =>
          errorFormatter(error)
        )

        if (syncResult.isErr()) {
          throw new Error(
            `Failed to load settings item 'REFERENCES', error: ${syncResult.error.message}`
          )
        }

        if (item.isNullObject) {
          return
        }

        const references: TextcutRectangle[] = JSON.parse(item.value)

        if (!Array.isArray(references)) {
          throw new Error(
            `Invalid references format in settings: ${item.value}`
          )
        }

        const refernceGroupMap = new Map<string, TextcutRectangle[]>()
        references.forEach((rect) => {
          if (rect.bindingGroupId == null) {
            if (!refernceGroupMap.has('NO_GROUP_ID')) {
              refernceGroupMap.set('NO_GROUP_ID', [])
            }
            refernceGroupMap.get('NO_GROUP_ID')!.push(rect)
          } else {
            if (!refernceGroupMap.has(rect.bindingGroupId)) {
              refernceGroupMap.set(rect.bindingGroupId, [])
            }
            refernceGroupMap.get(rect.bindingGroupId)!.push(rect)
          }
        })

        for (const [groupId, rects] of refernceGroupMap.entries()) {
          if (groupId === 'NO_GROUP_ID') {
            for (const rect of rects) {
              const worksheet = await getWorksheetFromPromise(ctx, rect.sheetId)
              if (worksheet.isErr()) {
                console.error('Failed to get worksheet:', worksheet.error)
                continue
              }
              const range = await getRangeByWorksheet(
                ctx,
                worksheet.value,
                rect.rangeAddr,
                ['address']
              )

              if (range.isErr()) {
                console.error('Failed to get range:', range.error)
                continue
              }

              const crerateBindingResult = await createExcelBinding({
                ctx,
                id: rect.bindingId,
                range: range.value,
                type: Excel.BindingType.range,
                onSelectionChanged: () =>
                  publishOnReferenceSelectedEvent(rect.bindingId),
                onDataChanged: () =>
                  publishBindingOnDataChangeEvent(rect.bindingId),
              })

              if (crerateBindingResult.isErr()) {
                console.error(
                  `Failed to create binding for ${rect.bindingId}: ${crerateBindingResult.error}`
                )
                continue
              }
            }
          } else {
            const worksheet = await getWorksheetFromPromise(
              ctx,
              rects[0].sheetId
            )
            if (worksheet.isErr()) {
              console.error('Failed to get worksheet:', worksheet.error)
              continue
            }
            const range = await getRangeByWorksheet(
              ctx,
              worksheet.value,
              rects[0].rangeAddr,
              ['address']
            )

            if (range.isErr()) {
              console.error('Failed to get range:', range.error)
              continue
            }
            const createBindingGroupResult = await createExcelBinding({
              ctx,
              id: rects[0].bindingId,
              range: range.value,
              type: Excel.BindingType.range,
              onSelectionChanged: () =>
                publishOnReferenceSelectedEvent(rects[0].bindingId),
              onDataChanged: () =>
                publishBindingOnDataChangeEvent(rects[0].bindingId),
            })

            if (createBindingGroupResult.isErr()) {
              console.error(
                `Failed to create binding group for ${groupId}: ${createBindingGroupResult.error}`
              )
              continue
            }
          }
        }

        syncResult = await ResultAsync.fromPromise(ctx.sync(), (error) =>
          errorFormatter(error)
        )

        if (syncResult.isErr()) {
          throw new Error(
            `Failed to re-create bindings, error: ${syncResult.error.message}`
          )
        }
      }))(),
    (error) => errorFormatter(error)
  )

export const revalidateBindings = async () => {
  // const removeAllBindingsResult = await removeAllBindings()

  // if (removeAllBindingsResult.isErr()) {
  //   throw new Error(
  //     `Failed to remove all bindings, error: ${removeAllBindingsResult.error.message}`
  //   )
  // }

  // re-create all bindings
  const recreateBindingsResult = await recreateBindings()

  if (recreateBindingsResult.isErr()) {
    throw new Error(
      `Failed to re-create bindings, error: ${recreateBindingsResult.error.message}`
    )
  }
}
